import React, { useState, useCallback, useEffect } from 'react';

const UnicodeGenerator = () => {
  const [existingSymbols, setExistingSymbols] = useState(new Set());
  const [generatedSymbols, setGeneratedSymbols] = useState([]);
  const [symbolCount, setSymbolCount] = useState(1);
  const [error, setError] = useState(null);
  const [fileContent, setFileContent] = useState(null);
  const [generationType, setGenerationType] = useState('random');
  const [selectedSection, setSelectedSection] = useState('');
  const [availableSections, setAvailableSections] = useState([]);
  const [sectionsData, setSectionsData] = useState(new Map());
  const [pastedSymbol, setPastedSymbol] = useState('');
  const [currentSymbolsView, setCurrentSymbolsView] = useState('table');
  const [generatedSymbolsView, setGeneratedSymbolsView] = useState('table');
  const [isSectionDropdownOpen, setIsSectionDropdownOpen] = useState(false);

  // Add this useEffect for handling clicks outside the dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.relative')) {
        setIsSectionDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);  

  // Function to get info about a symbol
  const getSymbolInfo = (symbol) => {
    if (!symbol) return null;
    const codePoint = symbol.codePointAt(0);
    const hexCode = codePoint.toString(16).toUpperCase();
    let section = '';
    let fileName = '';
    
    if (fileContent?.providers) {
      const provider = fileContent.providers.find(p => p.chars?.includes(symbol));
      if (provider) {
        section = provider.file.split(':')[1]?.split('/').slice(0, -1).join('/') || '';
        fileName = provider.file.split('/').pop() || '';
      }
    }
  
    return {
      symbol,
      unicode: `U+${hexCode}`,
      section,
      fileName,
      exists: fileContent?.providers?.some(p => p.chars?.includes(symbol)) || false
    };
  };
  

  // Function to check if symbol is used in any section
  const isSymbolUsedAnywhere = (symbol) => {
    for (const [_, symbols] of sectionsData) {
      if (symbols.has(symbol)) return true;
    }
    return existingSymbols.has(symbol);
  };

  // Function to get the next available Unicode in a range
  const findNextAvailable = (startCode, count = 1) => {
    const found = new Set();
    let current = startCode;
    
    while (found.size < count && current <= 0xF8FF) {
      const symbol = String.fromCodePoint(current);
      if (!isSymbolUsedAnywhere(symbol)) {
        found.add(symbol);
      }
      current++;
    }
    
    return Array.from(found);
  };

  // Function to get range based on section
  const getSectionRange = (section) => {
    if (section.includes('prefixes')) {
      return 0xE400;
    } else if (section.includes('emotes')) {
      return 0xE500;
    }
    return 0xE000;
  };

  // Handle symbol paste
  const handleSymbolPaste = (e) => {
    const value = e.target.value;
    setPastedSymbol(value);
    setError(null); // Clear any previous errors
  };

  // Parse JSON file
  const parseJsonFile = useCallback((content) => {
    try {
      const jsonData = JSON.parse(content);
      const symbols = new Set();
      const sections = new Map();
      
      if (jsonData.providers && Array.isArray(jsonData.providers)) {
        jsonData.providers.forEach(provider => {
          if (provider.file) {
            const section = provider.file.split(':')[1]?.split('/').slice(0, -1).join('/');
            if (section) {
              if (!sections.has(section)) {
                sections.set(section, new Set());
              }
              if (provider.chars && Array.isArray(provider.chars)) {
                provider.chars.forEach(char => {
                  if (char) {
                    symbols.add(char);
                    sections.get(section).add(char);
                  }
                });
              }
            }
          }
        });
      }

      setExistingSymbols(symbols);
      setAvailableSections(Array.from(sections.keys()));
      setFileContent(jsonData);
      setSectionsData(sections);
      setError(null);
    } catch (err) {
      console.error('Parse error:', err);
      setError('Failed to parse JSON file. Please make sure it\'s a valid default.json file.');
    }
  }, []);

  // Handle file upload
  const handleFileUpload = useCallback((event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      parseJsonFile(e.target.result);
    };
    reader.onerror = () => {
      setError('Failed to read the file.');
    };
    reader.readAsText(file);
  }, [parseJsonFile]);

  // Generate symbols
  const generateSymbols = useCallback(() => {
    if (generationType === 'random') {
      const maxAttempts = 1000;
      const newSymbols = [];
      let attempts = 0;

      while (newSymbols.length < symbolCount && attempts < maxAttempts) {
        const randomChar = String.fromCodePoint(0xE000 + Math.floor(Math.random() * (0xF8FF - 0xE000 + 1)));
        
        if (!isSymbolUsedAnywhere(randomChar)) {
          newSymbols.push(randomChar);
        }
        
        attempts++;
      }

      if (newSymbols.length < symbolCount) {
        setError('Failed to generate enough unique symbols. Too many symbols are already in use.');
      } else {
        setError(null);
      }

      setGeneratedSymbols(newSymbols);
    } else {
      if (!selectedSection) {
        setError('Please select a section first');
        return;
      }

      const sectionStart = getSectionRange(selectedSection);
      const newSymbols = findNextAvailable(sectionStart, symbolCount);

      if (newSymbols.length < symbolCount) {
        setError('Not enough available slots in this section');
      } else {
        setError(null);
      }

      setGeneratedSymbols(newSymbols);
    }
  }, [generationType, selectedSection, symbolCount]);

  return (
    <div className="min-h-screen bg-gray-100 py-6 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white shadow-xl rounded-lg overflow-hidden">
          <div className="px-6 py-8 bg-gradient-to-r from-[#2461f1] to-[#8044f6] sm:px-10 text-center">
            <h1 className="text-4xl font-bold text-white">Unicode Symbol Generator</h1>
          </div>

          <div className="px-6 py-8 sm:px-10">
            <div className="space-y-6">
              {error && (
                <div className="bg-red-50 border-l-4 border-red-400 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Paste Symbol for Info
                </label>
                <input
                  type="text"
                  value={pastedSymbol}
                  onChange={handleSymbolPaste}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Paste a symbol here"
                />
                {pastedSymbol && getSymbolInfo(pastedSymbol) && (
                  <div className="mt-2 bg-gray-50 rounded-lg p-3">
                    <table className="min-w-full divide-y divide-gray-200">
                      <tbody className="bg-white divide-y divide-gray-200">
                        <tr>
                          <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-500">Symbol</td>
                          <td className="px-4 py-2 whitespace-nowrap font-mono">{pastedSymbol}</td>
                        </tr>
                        <tr>
                          <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-500">Unicode</td>
                          <td className="px-4 py-2 whitespace-nowrap font-mono">
                            U+{pastedSymbol.codePointAt(0).toString(16).toUpperCase()}
                          </td>
                        </tr>
                        {fileContent && (
                          <>
                            <tr>
                              <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-500">Section</td>
                              <td className="px-4 py-2 whitespace-nowrap">
                                {getSymbolInfo(pastedSymbol)?.section || 'Not found in default.json'}
                              </td>
                            </tr>
                            <tr>
                              <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-500">File</td>
                              <td className="px-4 py-2 whitespace-nowrap">
                                {getSymbolInfo(pastedSymbol)?.fileName || 'Not found in default.json'}
                              </td>
                            </tr>
                          </>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-4">
                  Generation Type
                </label>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="random"
                      name="generationType"
                      value="random"
                      checked={generationType === 'random'}
                      onChange={(e) => setGenerationType(e.target.value)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                    />
                    <label htmlFor="random" className="ml-2 text-sm text-gray-700">
                      Random Unicode Generation
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="structured"
                      name="generationType"
                      value="structured"
                      checked={generationType === 'structured'}
                      onChange={(e) => setGenerationType(e.target.value)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                    />
                    <label htmlFor="structured" className="ml-2 text-sm text-gray-700">
                      Structured Generation (Based on default.json)
                    </label>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Upload your default.json {generationType === 'structured' ? '(Required)' : '(Optional)'}
                </label>
                <div className="mt-1">
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleFileUpload}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                </div>
                <p className="mt-2 text-sm text-gray-500">
                  {generationType === 'structured' 
                    ? 'Required to analyze sections and generate structured unicodes' 
                    : 'Upload to avoid generating already used symbols'}
                </p>
              </div>

              {generationType === 'structured' && availableSections.length > 0 && (
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700">
                    Select Section
                  </label>
                  <div className="relative mt-1">
                    <input
                      type="text"
                      value={selectedSection}
                      onChange={(e) => {
                        setSelectedSection(e.target.value);
                        if (!isSectionDropdownOpen) {
                          setIsSectionDropdownOpen(true);
                        }
                      }}
                      onFocus={() => setIsSectionDropdownOpen(true)}
                      className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                      placeholder="Select or type to search sections"
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                      <button
                        type="button"
                        onClick={() => setIsSectionDropdownOpen(!isSectionDropdownOpen)}
                        className="p-1 hover:bg-gray-100 rounded-full"
                      >
                        <svg 
                          className={`h-5 w-5 text-gray-400 transform transition-transform ${isSectionDropdownOpen ? 'rotate-180' : ''}`} 
                          fill="none" 
                          viewBox="0 0 24 24" 
                          stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    </div>

                    {isSectionDropdownOpen && (
                      <div className="absolute z-10 mt-1 w-full bg-gray-800 shadow-lg max-h-60 rounded-md py-1 text-base overflow-auto scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800 focus:outline-none sm:text-sm">
                        {availableSections
                          .filter(section => section.toLowerCase().includes(selectedSection.toLowerCase()))
                          .map((section) => (
                            <div
                              key={section}
                              className={`cursor-pointer select-none relative py-2 pl-3 pr-9 text-white hover:bg-gray-700 ${
                                section === selectedSection ? 'bg-gray-700' : ''
                              }`}
                              onClick={() => {
                                setSelectedSection(section);
                                setIsSectionDropdownOpen(false);
                              }}
                            >
                              {section}
                              {section === selectedSection && (
                                <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-400">
                                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </span>
                              )}
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

{fileContent && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="text-sm font-medium text-gray-700">
                      {selectedSection ? `Symbols in ${selectedSection}` : 'All Current Symbols'}
                    </h3>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-500">
                        {selectedSection ? sectionsData.get(selectedSection)?.size || 0 : existingSymbols.size} symbols
                      </span>
                      <button
                        onClick={() => setCurrentSymbolsView(currentSymbolsView === 'table' ? 'snippet' : 'table')}
                        className="px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded-md text-xs text-gray-600"
                      >
                        View as {currentSymbolsView === 'table' ? 'Snippet' : 'Table'}
                      </button>
                    </div>
                  </div>
                  {currentSymbolsView === 'table' ? (
                    <div className="bg-gray-800 rounded-lg p-4">
                      <div className="max-h-40 overflow-auto">
                        <table className="min-w-full divide-y divide-gray-700">
                          <thead className="bg-gray-800">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Symbol</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Unicode</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">File</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-700">
                            {Array.from(selectedSection ? sectionsData.get(selectedSection) || [] : existingSymbols).map((symbol, index) => {
                              const file = fileContent.providers.find(p => p.chars?.includes(symbol))?.file;
                              const fileName = file ? file.split('/').pop() : 'Unknown';
                              return (
                                <tr key={index}>
                                  <td className="px-6 py-4 whitespace-nowrap font-mono text-white">{symbol}</td>
                                  <td className="px-6 py-4 whitespace-nowrap font-mono text-white">U+{symbol.codePointAt(0).toString(16).toUpperCase()}</td>
                                  <td className="px-6 py-4 whitespace-nowrap text-white">{fileName}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <pre className="bg-gray-800 text-white p-4 rounded-lg text-sm overflow-x-auto max-h-40">
                      {Array.from(selectedSection ? sectionsData.get(selectedSection) || [] : existingSymbols).map((symbol, index) => {
                        const file = fileContent.providers.find(p => p.chars?.includes(symbol))?.file;
                        const fileName = file ? file.split('/').pop() : 'Unknown';
                        return (
                          <div key={index} className="font-mono">
                            {symbol} (U+{symbol.codePointAt(0).toString(16).toUpperCase()}) - {fileName}
                          </div>
                        );
                      })}
                    </pre>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Number of Symbols to Generate: {symbolCount}
                </label>
                <input
                  type="range"
                  min="1"
                  max="100"
                  value={symbolCount}
                  onChange={(e) => setSymbolCount(parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
              </div>

              <button
                onClick={generateSymbols}
                disabled={generationType === 'structured' && !selectedSection}
                className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                  generationType === 'structured' && !selectedSection
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-[#0747ca] hover:bg-[#0747ca]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0747ca]'
                }`}
              >
                Generate Symbols
              </button>

              {generatedSymbols.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="text-sm font-medium text-gray-700">Generated Symbols</h3>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setGeneratedSymbolsView(generatedSymbolsView === 'table' ? 'snippet' : 'table')}
                        className="px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded-md text-xs text-gray-600"
                      >
                        View as {generatedSymbolsView === 'table' ? 'Snippet' : 'Table'}
                      </button>
                      <button
                        onClick={() => navigator.clipboard.writeText(generatedSymbols.map(symbol => {
                          const info = getSymbolInfo(symbol);
                          return `Symbol: ${symbol}\nUnicode: U+${symbol.codePointAt(0).toString(16).toUpperCase()}${
                            info?.section ? `\nSection: ${info.section}\nFile: ${info.fileName}` : ''
                          }`;
                        }).join('\n\n'))}
                        className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded-md text-sm text-gray-600"
                      >
                        Copy All
                      </button>
                    </div>
                  </div>
                  {generatedSymbolsView === 'table' ? (
                    <div className="bg-gray-800 rounded-lg p-4">
                      <div className="max-h-40 overflow-auto scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800">
                        <table className="min-w-full divide-y divide-gray-700">
                          <thead className="bg-gray-800">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Symbol</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Unicode</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-700">
                            {generatedSymbols.map((symbol, index) => (
                              <tr key={index}>
                                <td className="px-6 py-4 whitespace-nowrap font-mono text-white">{symbol}</td>
                                <td className="px-6 py-4 whitespace-nowrap font-mono text-white">U+{symbol.codePointAt(0).toString(16).toUpperCase()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <pre className="bg-gray-800 text-white p-4 rounded-lg text-sm overflow-x-auto max-h-40">
                      {generatedSymbols.map((symbol, index) => {
                        const info = getSymbolInfo(symbol);
                        return (
                          <div key={index} className="whitespace-pre-line">
                            Symbol: {symbol}{'\n'}
                            Unicode: U+{symbol.codePointAt(0).toString(16).toUpperCase()}
                            {info?.section && `\nSection: ${info.section}`}
                            {index < generatedSymbols.length - 1 && <div className="mt-2"></div>}
                          </div>
                        );
                      })}
                    </pre>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <footer className="mt-8 text-center text-sm text-gray-500">
        Created by Kaludi.
      </footer>
    </div>
  );
};

export default UnicodeGenerator;